import type { SortingDashboardItem } from '../../../graphql/types/dashboard/SortingDashboardTypes';

/** 错分折叠子行：件数占比分母与主错分率一致（inbound_wrong_sort_denominator_count） */
export const WRONG_SORT_SUBMETRIC_RATE_FIELDS = new Set<string>([
    'wrongSortToWrongLineABCount',
    'wrongSortToWrongHubACount',
    'wrongSortToWrongWhsBCount',
    'wrongSortToWrongLineCCount',
]);

/**
 * 12h 出库分子：接口未下发 outbound_12h_count 时，由展示率 × 入库量反推（多行按 hub/周期合并时与 ∑分子/∑分母 一致）。
 */
export function outbound12hNumeratorFromItem(item: Partial<SortingDashboardItem>): number {
    const inbound = Number(item.inboundCount ?? 0);
    if (inbound === 0) return 0;
    const r = item.outbound12hRate;
    if (r == null || r === '' || r === '-') return 0;
    const pct = parseFloat(String(r).replace('%', ''));
    if (Number.isNaN(pct)) return 0;
    return (pct / 100) * inbound;
}

export function weightedNumeratorForSortingRate(
    item: SortingDashboardItem,
    field: string,
    numerators: (keyof SortingDashboardItem)[]
): number {
    if (field === 'outbound12hRate') return outbound12hNumeratorFromItem(item);
    return numerators.reduce((s, k) => s + Number(item[k] ?? 0), 0);
}

function ratePct(num: number, den: number): string | undefined {
    if (den === 0) return undefined;
    const pct = (num / den) * 100;
    const rounded = Math.round(pct * 100) / 100;
    return `${rounded.toFixed(2)}%`;
}

/**
 * 同一 timePeriod + 同一 hub（或同一 sortingCode 过滤结果）下多行（如多 customer_name）合并为一行展示口径：件数字段求和，率类按总分子/总分母重算。
 */
export function mergeSortingDashboardItemsForDisplay(rows: SortingDashboardItem[]): SortingDashboardItem {
    if (rows.length === 0) {
        throw new Error('mergeSortingDashboardItemsForDisplay: empty rows');
    }
    if (rows.length === 1) return rows[0];

    const base = rows[0];
    const sum = (pick: (r: SortingDashboardItem) => number) =>
        rows.reduce((s, r) => s + pick(r), 0);

    const inboundCount = sum((r) => Number(r.inboundCount ?? 0));
    const inboundWrongSortDenominatorCount = sum((r) => Number(r.inboundWrongSortDenominatorCount ?? 0));
    const shouldArriveNotArriveCount = sum((r) => Number(r.shouldArriveNotArriveCount ?? 0));
    const outbound12hNum = sum((r) => outbound12hNumeratorFromItem(r));
    const transferArrivalDscanCount = sum((r) => Number(r.transferArrivalDscanCount ?? 0));
    const transferArrivalMissCount = sum((r) => Number(r.transferArrivalMissCount ?? 0));
    const dscanCount = sum((r) => Number(r.dscanCount ?? 0));
    const ascanMissCount = sum((r) => Number(r.ascanMissCount ?? 0));
    const outboundMissCount = sum((r) => Number(r.outboundMissCount ?? 0));
    const wrongSortCount = sum((r) => Number(r.wrongSortCount ?? 0));
    const wrongSortToWrongHubACount = sum((r) => Number(r.wrongSortToWrongHubACount ?? 0));
    const wrongSortToWrongWhsBCount = sum((r) => Number(r.wrongSortToWrongWhsBCount ?? 0));
    const wrongSortToWrongLineCCount = sum((r) => Number(r.wrongSortToWrongLineCCount ?? 0));
    const lostDamagedCount = sum((r) => Number(r.lostDamagedCount ?? 0));
    const notCorrectlyPackedCount = sum((r) => Number(r.notCorrectlyPackedCount ?? 0));
    const bagLabelNotShippedCount = sum((r) => Number(r.bagLabelNotShippedCount ?? 0));
    const noDepartureActionCount = sum((r) => Number(r.noDepartureActionCount ?? 0));
    const crossBorderOnline24hCount = sum((r) => Number(r.crossBorderOnline24hCount ?? 0));
    const crossBorderHandoverCount = sum((r) => Number(r.crossBorderHandoverCount ?? 0));
    const sortPeak = Math.max(...rows.map((r) => Number(r.sortPeak ?? 0)));

    let effWeighted = 0;
    let effDenom = 0;
    rows.forEach((r) => {
        const inc = Number(r.inboundCount ?? 0);
        if (inc <= 0) return;
        const raw = r.sortEfficiency;
        if (raw == null || raw === '') return;
        const n = parseFloat(String(raw).replace(/,/g, ''));
        if (!Number.isNaN(n)) {
            effWeighted += n * inc;
            effDenom += inc;
        }
    });
    const sortEfficiency =
        effDenom > 0 ? (Math.round((effWeighted / effDenom) * 10) / 10).toFixed(1) : base.sortEfficiency;

    const merged: SortingDashboardItem = {
        ...base,
        inboundCount,
        inboundWrongSortDenominatorCount,
        shouldArriveNotArriveCount,
        shouldArriveNotArriveRate: ratePct(shouldArriveNotArriveCount, inboundCount),
        outbound12hRate: ratePct(outbound12hNum, inboundCount),
        transferArrivalDscanCount,
        transferArrivalMissCount,
        transferArrivalMissRate: ratePct(transferArrivalMissCount, transferArrivalDscanCount),
        dscanCount,
        ascanMissCount,
        ascanMissRate: ratePct(ascanMissCount, dscanCount),
        outboundMissCount,
        outboundMissRate: ratePct(outboundMissCount, inboundCount),
        wrongSortCount,
        wrongSortRate: ratePct(wrongSortCount, inboundWrongSortDenominatorCount),
        wrongSortToWrongHubACount,
        wrongSortToWrongWhsBCount,
        wrongSortToWrongLineCCount,
        lostDamagedCount,
        lostDamagedRate: ratePct(lostDamagedCount, inboundCount),
        notCorrectlyPackedCount,
        bagLabelNotShippedCount,
        noDepartureActionCount,
        crossBorderOnline24hCount,
        crossBorderHandoverCount,
        crossBorderOnline24hRate: ratePct(crossBorderOnline24hCount, crossBorderHandoverCount),
        sortEfficiency: sortEfficiency ?? undefined,
        sortPeak,
    };

    return merged;
}

/**
 * 同一 timePeriod 下多行 hubId=0 合并为「全美」指标行，与表格列对 hub0 多行求和/重算率口径一致，避免按行 forEach 时 last-write-wins。
 */
export function mergedHub0MetricRowForDisplay(
    samePeriodHub0Items: SortingDashboardItem[],
    getMetricFieldValue: (item: Partial<SortingDashboardItem>, field: string) => unknown,
    sortingFields: readonly string[]
): Record<string, unknown> {
    if (samePeriodHub0Items.length === 0) {
        return {};
    }
    const merged = mergeSortingDashboardItemsForDisplay(samePeriodHub0Items);
    const row: Record<string, unknown> = {};
    sortingFields.forEach((field) => {
        const v = getMetricFieldValue(merged, field);
        if (v !== undefined && v !== null && v !== '' && v !== '-') {
            row[field] = v;
        }
    });
    return row;
}
